import React from "react";
import { useIsMac } from "@/hooks/useIsMac";
import { Keyboard } from "lucide-react";
import { motion, useReducedMotion } from "motion/react";
import { Button } from "@/components/ui/button";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import { ShortcutKeys } from "@/components/ui/shortcut-keys";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";

/**
 * The anvil's springy overshoot curve (`anvil-enter` in globals.css), on the
 * snappy hover beat ScrollToBottomButton uses: hover feedback tracks the
 * pointer, so any lag reads as the pill being unresponsive.
 */
export const HOVER_SPRING = {
  duration: 0.12,
  ease: [0.34, 1.56, 0.64, 1],
} as const;

/** The 36px frosted-glass circle the floating pills share (overrides the
 *  design-system pill's pinned 32px box, padding, and flat colors). */
export const FLOATING_CIRCLE_BUTTON_CLASSES = cn(
  "!h-9 !min-h-9 !max-h-9 w-9 !p-0",
  "border-black/10 bg-white/70 shadow-sm backdrop-blur-md hover:border-black/10 hover:bg-white dark:border-white/15 dark:bg-neutral-900/70 dark:hover:border-white/15 dark:hover:bg-neutral-900",
);

export interface ShortcutsPillProps {
  /** Force the hover card open (useful for demos and visual tests). */
  open?: boolean;
  modelSwitcherEnabled?: boolean;
  className?: string;
}

interface CodesmithShortcut {
  keys: string[];
  description: string;
}

function getCodesmithShortcuts(
  isMac: boolean,
  modelSwitcherEnabled: boolean,
): CodesmithShortcut[] {
  const opt = isMac ? "⌥" : "Alt";
  const cmd = isMac ? "⌘" : "Ctrl";
  return [
    { keys: ["C"], description: "Open [code]smith" },
    { keys: [opt, "⇧", "N"], description: "New chat" },
    { keys: [opt, "B"], description: "Toggle chat list" },
    { keys: [opt, "L"], description: "Focus chat input" },
    { keys: [`${opt} ×2`], description: "Session switcher" },
    { keys: [opt, "D"], description: "Archive session" },
    { keys: [opt, "G"], description: "Open session's PRs" },
    { keys: [opt, "P"], description: "Open PR previews" },
    { keys: [cmd, "↵"], description: "Queue message" },
    ...(modelSwitcherEnabled
      ? [{ keys: [cmd, "⇧", "."], description: "Toggle Fast/Deep mode" }]
      : []),
  ];
}

function ShortcutsPanel({
  modelSwitcherEnabled,
}: {
  modelSwitcherEnabled: boolean;
}) {
  const isMac = useIsMac();
  return (
    <div className="w-72 rounded-xl border border-[#0c0c0d14] bg-white p-3 shadow-[0px_10px_20px_0px_rgba(0,0,0,0.12)] dark:border-white/12 dark:bg-[#2A2C2C] dark:shadow-[0px_10px_20px_0px_rgba(0,0,0,0.2)]">
      <div className="mb-2 flex items-center gap-1.5">
        <Keyboard className="size-3.5 text-neutral-500 dark:text-neutral-400" />
        <span className="text-xs font-medium text-neutral-800 dark:text-neutral-100">
          [code]smith shortcuts
        </span>
      </div>
      <div className="space-y-1.5">
        {getCodesmithShortcuts(isMac, modelSwitcherEnabled).map((shortcut) => (
          <div
            key={shortcut.description}
            className="flex items-center justify-between gap-x-4"
          >
            <span className="text-xs text-neutral-600 dark:text-neutral-300">
              {shortcut.description}
            </span>
            <ShortcutKeys keys={shortcut.keys} />
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Floating glass pill anchored in the [code]smith chat pane; hovering it
 * reveals the [code]smith keyboard shortcuts.
 */
export function ShortcutsPill({
  open,
  modelSwitcherEnabled = false,
  className,
}: ShortcutsPillProps) {
  const prefersReducedMotion = useReducedMotion();
  const isMobile = useIsMobile();
  // Keyboard shortcuts don't apply on mobile, so neither the pill nor its
  // expanded shortcuts panel should show there.
  if (isMobile) return null;
  return (
    <HoverCard open={open} openDelay={100} closeDelay={150}>
      <HoverCardTrigger asChild>
        <Button
          asChild
          variant="secondary"
          type="button"
          aria-label="[code]smith keyboard shortcuts"
          className={cn(FLOATING_CIRCLE_BUTTON_CLASSES, className)}
        >
          <motion.button
            whileHover={
              prefersReducedMotion ? undefined : { scale: 1.1, rotate: -6 }
            }
            whileTap={prefersReducedMotion ? undefined : { scale: 0.92 }}
            transition={HOVER_SPRING}
          >
            <Keyboard className="size-4 text-neutral-600 dark:text-neutral-300" />
          </motion.button>
        </Button>
      </HoverCardTrigger>
      <HoverCardContent
        side="top"
        align="end"
        sideOffset={8}
        // The anvil's motion language (`anvil-enter`/`anvil-exit` in
        // globals.css) on a quicker hover beat. Exit must run on this Radix
        // content node: Radix defers unmount until its animation finishes.
        className={cn(
          "w-auto border-none bg-transparent p-0 shadow-none dark:bg-transparent",
          "data-[state=open]:animate-[anvil-enter_0.2s_cubic-bezier(0.34,1.56,0.64,1)_both]",
          "data-[state=closed]:animate-[anvil-exit_0.15s_cubic-bezier(0.36,0,0.66,-0.56)_both]",
          "motion-reduce:data-[state=closed]:animate-none motion-reduce:data-[state=open]:animate-none",
        )}
      >
        <ShortcutsPanel modelSwitcherEnabled={modelSwitcherEnabled} />
      </HoverCardContent>
    </HoverCard>
  );
}
